import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, Package, DollarSign, TrendingUp, CheckCircle, Building2, FileText, Clock, ShieldAlert, Handshake, Eye, Trash2, FileCheck2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import FCOsAdminTab from '@/components/admin/FCOsAdminTab';
import ChecklistsAdminTab from '@/components/admin/ChecklistsAdminTab';
import { tStatus } from '@/i18n';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [pendingCompliance, setPendingCompliance] = useState([]);
  const [deals, setDeals] = useState([]);
  const [pendingListings, setPendingListings] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [listingFilter, setListingFilter] = useState('all'); // 'all' | 'pending_verification' | 'verified' | 'rejected'
  const [loading, setLoading] = useState(true);
  const [assayDialogOpen, setAssayDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [currentSummary, setCurrentSummary] = useState(null);
  const [assayForm, setAssayForm] = useState({ grade_result: '', purity_percentage: '', meets_specification: false, inspector_name: '', notes: '' });
  const { getAuthHeader } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/admin/users`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      try {
        const inspectionsRes = await axios.get(`${API_URL}/inspections/my-inspections`, { headers: getAuthHeader() });
        setInspections(inspectionsRes.data);
      } catch {}
      try {
        const complianceRes = await axios.get(`${API_URL}/compliance/offers/pending`, { headers: getAuthHeader() });
        setPendingCompliance(Array.isArray(complianceRes.data) ? complianceRes.data : complianceRes.data.offers || []);
      } catch {}
      try {
        const dealsRes = await axios.get(`${API_URL}/admin/deals`, { headers: getAuthHeader() });
        setDeals(dealsRes.data);
      } catch {}
      try {
        const listingsRes = await axios.get(`${API_URL}/admin/listings/pending`, { headers: getAuthHeader() });
        setPendingListings(listingsRes.data);
      } catch {}
      try {
        const allRes = await axios.get(`${API_URL}/admin/listings/all`, { headers: getAuthHeader() });
        setAllListings(allRes.data);
      } catch {}
    } catch { toast.error('Failed to fetch admin data'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleVerifyUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/verify`, {}, { headers: getAuthHeader() });
      toast.success('User verified successfully');
      fetchData();
    } catch { toast.error('Failed to verify user'); }
  };

  const handleComplianceReview = async (offerId, approved) => {
    try {
      await axios.post(`${API_URL}/compliance/offers/${offerId}/review`,
        { approved, notes: approved ? 'Approved by admin' : 'Rejected by admin' },
        { headers: getAuthHeader() }
      );
      toast.success(approved ? 'Offer compliance approved' : 'Offer compliance rejected');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to review compliance'); }
  };

  const handleVerifyDeal = async (dealId) => {
    try {
      await axios.put(`${API_URL}/deals/${dealId}/status`,
        { status: 'in_progress', notes: 'Deal verified and approved by admin' },
        { headers: getAuthHeader() }
      );
      toast.success(t('admin.deals.verified'));
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('error.generic')); }
  };

  const handleReviewListing = async (mineralId, status) => {
    try {
      await axios.post(`${API_URL}/admin/listings/${mineralId}/review`,
        { status, notes: status === 'verified' ? 'Listing approved by admin' : 'Listing rejected by admin' },
        { headers: getAuthHeader() }
      );
      toast.success(status === 'verified' ? t('admin.listings.approved') : t('admin.listings.rejected'));
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('error.generic')); }
  };

  const handleDeleteListing = async (mineralId, title) => {
    if (!window.confirm(t('admin.listings.deleteConfirm'))) return;
    try {
      await axios.delete(`${API_URL}/minerals/${mineralId}`, { headers: getAuthHeader() });
      toast.success(t('seller.listingDeleted'));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('error.generic')); }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(t('admin.users.deleteConfirm'))) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, { headers: getAuthHeader() });
      toast.success(t('admin.users.deleted'));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('error.generic')); }
  };

  const handleViewSummary = async (dealId) => {
    try {
      const res = await axios.get(`${API_URL}/deals/${dealId}/summary`, { headers: getAuthHeader() });
      setCurrentSummary(res.data);
      setSummaryDialogOpen(true);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to load deal summary'); }
  };

  const handleSubmitAssay = async (isRetest = false) => {
    if (!selectedInspection) return;
    try {
      const formData = new FormData();
      formData.append('grade_result', assayForm.grade_result);
      formData.append('purity_percentage', parseFloat(assayForm.purity_percentage));
      formData.append('meets_specification', assayForm.meets_specification);
      formData.append('inspector_name', assayForm.inspector_name);
      formData.append('notes', assayForm.notes);
      const endpoint = isRetest ? `${API_URL}/inspections/${selectedInspection.id}/final-assay` : `${API_URL}/inspections/${selectedInspection.id}/preliminary-assay`;
      await axios.post(endpoint, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      toast.success(`${isRetest ? 'Final' : 'Preliminary'} assay results submitted`);
      setAssayDialogOpen(false);
      setAssayForm({ grade_result: '', purity_percentage: '', meets_specification: false, inspector_name: '', notes: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit assay results'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120]">
        <Navbar />
        <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F59E0B]"></div></div>
      </div>
    );
  }

  const pendingDeals = deals.filter(d => d.status === 'pending_verification');
  const statCards = [
    { icon: Users, label: t('admin.stats.users'), value: stats?.total_users || 0, color: 'text-[#3B82F6]', bg: 'bg-[#3B82F6]/10' },
    { icon: Package, label: t('admin.stats.listings'), value: stats?.total_minerals || 0, color: 'text-[#10B981]', bg: 'bg-[#10B981]/10' },
    { icon: TrendingUp, label: t('admin.stats.deals'), value: stats?.total_transactions || 0, color: 'text-[#8B5CF6]', bg: 'bg-[#8B5CF6]/10' },
    { icon: DollarSign, label: t('admin.stats.gmv'), value: `$${(stats?.total_commission || 0).toFixed(2)}`, color: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/10' },
  ];

  const statusStyle = (status) => {
    const map = {
      pending_verification: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20',
      verified: 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20',
      in_progress: 'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/20',
      completed: 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20',
      cancelled: 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20',
    };
    return map[status] || 'bg-white/5 text-[#94A3B8] border-[#1E293B]';
  };

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-8 font-['Outfit']" data-testid="admin-dashboard-title">{t('admin.title')}</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map(({ icon: Icon, label, value, color, bg }, i) => (
            <div key={i} className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-6" data-testid={`stat-${label.toLowerCase().replace(' ', '-')}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 ${bg} rounded-lg flex items-center justify-center`}><Icon className={`w-5 h-5 ${color}`} /></div>
                <span className="text-2xl font-bold text-white">{value}</span>
              </div>
              <p className="text-[#64748B] text-sm font-medium">{label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="mb-6 bg-[#121A2F] border border-[#1E293B]">
            <TabsTrigger value="users" data-testid="users-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]"><Users className="w-4 h-4 mr-2" />{t('admin.tabs.users')}</TabsTrigger>
            <TabsTrigger value="listings" data-testid="listings-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]">
              <Package className="w-4 h-4 mr-2" />{t('admin.tabs.listings')} {pendingListings.length > 0 && <span className="ml-1 bg-[#EF4444] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center" data-testid="pending-listings-badge">{pendingListings.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="deals" data-testid="deals-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]">
              <Handshake className="w-4 h-4 mr-2" />{t('admin.tabs.deals')} {pendingDeals.length > 0 && <span className="ml-1 bg-[#EF4444] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{pendingDeals.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="compliance" data-testid="compliance-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]">
              <ShieldAlert className="w-4 h-4 mr-2" />{t('admin.tabs.compliance')} {pendingCompliance.length > 0 && <span className="ml-1 bg-[#EF4444] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{pendingCompliance.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="inspections" data-testid="inspections-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]"><Building2 className="w-4 h-4 mr-2" />{t('inspection.title')}</TabsTrigger>
            <TabsTrigger value="fcos" data-testid="fcos-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]"><FileCheck2 className="w-4 h-4 mr-2" />FCOs</TabsTrigger>
            <TabsTrigger value="checklists" data-testid="checklists-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]"><ShieldAlert className="w-4 h-4 mr-2" />Checklists</TabsTrigger>
          </TabsList>

          <TabsContent value="users" data-testid="users-content">
            <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#1A2440] border-b border-[#1E293B]">
                    <tr>
                      {['Name', 'Email', 'Role', 'Status', 'Actions'].map(h => (
                        <th key={h} className="px-6 py-4 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-[#1E293B] hover:bg-white/[0.02] transition-colors" data-testid={`user-row-${user.id}`}>
                        <td className="px-6 py-4 text-sm text-white">{user.name}</td>
                        <td className="px-6 py-4 text-sm text-[#94A3B8]">{user.email}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            user.role === 'buyer' ? 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20' :
                            user.role === 'seller' ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' :
                            'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/20'
                          }`}>{user.role.toUpperCase()}</span>
                        </td>
                        <td className="px-6 py-4">
                          {user.verified ? (
                            <span className="inline-flex items-center text-sm text-[#10B981]"><CheckCircle className="w-4 h-4 mr-1" />Verified</span>
                          ) : (
                            <span className="text-sm text-[#64748B]">Not Verified</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {!user.verified && user.role === 'seller' && (
                              <Button onClick={() => handleVerifyUser(user.id)} data-testid={`verify-user-${user.id}`} size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white">Verify</Button>
                            )}
                            <Button onClick={() => handleDeleteUser(user.id, user.email)} data-testid={`delete-user-${user.id}`} size="sm" className="bg-transparent border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="listings" data-testid="listings-content">
            {/* Filter bar */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {[
                { v: 'all', label: 'All', count: allListings.length },
                { v: 'pending_verification', label: 'Pending', count: allListings.filter(l => l.listing_status === 'pending_verification').length },
                { v: 'verified', label: 'Verified', count: allListings.filter(l => l.listing_status === 'verified').length },
                { v: 'rejected', label: 'Rejected', count: allListings.filter(l => l.listing_status === 'rejected').length },
              ].map(f => (
                <Button
                  key={f.v}
                  onClick={() => setListingFilter(f.v)}
                  data-testid={`filter-listings-${f.v}`}
                  size="sm"
                  className={`${listingFilter === f.v ? 'bg-[#F59E0B] text-[#0B1120]' : 'bg-[#1A2440] text-[#94A3B8] border border-[#1E293B] hover:bg-white/5'} font-medium`}
                >
                  {f.label} ({f.count})
                </Button>
              ))}
            </div>

            <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden">
              {(() => {
                const filtered = listingFilter === 'all'
                  ? allListings
                  : allListings.filter(l => (l.listing_status || 'verified') === listingFilter);
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <Package className="w-12 h-12 text-[#1E293B] mx-auto mb-4" />
                      <p className="text-[#94A3B8]">No listings in this view</p>
                    </div>
                  );
                }
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#1A2440] border-b border-[#1E293B]">
                        <tr>
                          {['Title', 'Seller', 'Category', 'Price', 'Quantity', 'Location', 'Status', 'Actions'].map(h => (
                            <th key={h} className="px-5 py-4 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((listing) => {
                          const status = listing.listing_status || 'verified';
                          return (
                            <tr key={listing.id} className="border-b border-[#1E293B] hover:bg-white/[0.02] transition-colors" data-testid={`listing-row-${listing.id}`}>
                              <td className="px-5 py-4 text-sm font-medium text-white">{listing.title}</td>
                              <td className="px-5 py-4 text-sm text-[#94A3B8]">{listing.seller_name}</td>
                              <td className="px-5 py-4 text-sm text-[#94A3B8]">{listing.category?.replace('_', ' ')}</td>
                              <td className="px-5 py-4 text-sm text-white">${listing.price?.toLocaleString()}/{listing.unit}</td>
                              <td className="px-5 py-4 text-sm text-[#94A3B8]">{listing.quantity} {listing.unit}</td>
                              <td className="px-5 py-4 text-sm text-[#94A3B8]">{listing.location}</td>
                              <td className="px-5 py-4">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                                  status === 'verified' ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' :
                                  status === 'rejected' ? 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20' :
                                  'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20'
                                }`}>{tStatus(status)}</span>
                              </td>
                              <td className="px-5 py-4">
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => window.open(`/mineral/${listing.id}`, '_blank')}
                                    data-testid={`view-listing-${listing.id}`}
                                    size="sm"
                                    className="bg-[#1A2440] text-[#94A3B8] border border-[#1E293B] hover:bg-white/5"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </Button>
                                  {status === 'pending_verification' && (
                                    <>
                                      <Button onClick={() => handleReviewListing(listing.id, 'verified')} data-testid={`approve-listing-${listing.id}`} size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white">Approve</Button>
                                      <Button onClick={() => handleReviewListing(listing.id, 'rejected')} data-testid={`reject-listing-${listing.id}`} size="sm" className="bg-transparent border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10">Reject</Button>
                                    </>
                                  )}
                                  <Button onClick={() => handleDeleteListing(listing.id, listing.title)} data-testid={`delete-listing-${listing.id}`} size="sm" className="bg-transparent border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </TabsContent>

          <TabsContent value="deals" data-testid="deals-content">
            <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden">
              {deals.length === 0 ? (
                <div className="text-center py-12">
                  <Handshake className="w-12 h-12 text-[#1E293B] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">No deals yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1A2440] border-b border-[#1E293B]">
                      <tr>
                        {['Mineral', 'Buyer', 'Seller', 'Price/Unit', 'Qty', 'Total', 'Status', 'Actions'].map(h => (
                          <th key={h} className="px-5 py-4 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {deals.map((deal) => (
                        <tr key={deal.id} className="border-b border-[#1E293B] hover:bg-white/[0.02] transition-colors" data-testid={`deal-row-${deal.id}`}>
                          <td className="px-5 py-4 text-sm font-medium text-white">{deal.mineral_title}</td>
                          <td className="px-5 py-4 text-sm text-[#94A3B8]">{deal.buyer_name}</td>
                          <td className="px-5 py-4 text-sm text-[#94A3B8]">{deal.seller_name}</td>
                          <td className="px-5 py-4 text-sm text-white">${deal.agreed_price?.toLocaleString()}</td>
                          <td className="px-5 py-4 text-sm text-[#94A3B8]">{deal.quantity}</td>
                          <td className="px-5 py-4 text-sm font-medium text-[#F59E0B]">${deal.total_value?.toLocaleString()}</td>
                          <td className="px-5 py-4">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyle(deal.status)}`}>
                              {tStatus(deal.status)}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2">
                              {deal.status === 'pending_verification' && (
                                <Button onClick={() => handleVerifyDeal(deal.id)} data-testid={`verify-deal-${deal.id}`} size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white">
                                  <CheckCircle className="w-3.5 h-3.5 mr-1" /> Verify
                                </Button>
                              )}
                              {deal.status !== 'pending_verification' && (
                                <Button onClick={() => handleViewSummary(deal.id)} data-testid={`view-summary-${deal.id}`} size="sm" className="bg-[#1A2440] text-white border border-[#1E293B] hover:bg-white/5">
                                  <Eye className="w-3.5 h-3.5 mr-1" /> Summary
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Deal Summary Dialog */}
          <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
            <DialogContent className="bg-[#121A2F] border-[#1E293B] max-w-lg" data-testid="deal-summary-dialog">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#F59E0B]" /> Deal Agreement Summary
                </DialogTitle>
              </DialogHeader>
              {currentSummary && (
                <div className="mt-4 space-y-0 bg-[#0B1120] rounded-xl border border-[#1E293B] overflow-hidden">
                  <div className="bg-[#1A2440] px-5 py-3 border-b border-[#1E293B]">
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Deal #{currentSummary.deal_id?.slice(0, 8)}</p>
                    <p className="text-lg font-bold text-white">{currentSummary.mineral_title}</p>
                  </div>
                  <div className="divide-y divide-[#1E293B]">
                    {[
                      ['Mineral Type', currentSummary.mineral_type],
                      ['Quantity', `${currentSummary.quantity} ${currentSummary.unit}`],
                      ['Price per Unit', `$${currentSummary.agreed_price_per_unit?.toLocaleString()} ${currentSummary.currency}`],
                      ['Total Value', `$${currentSummary.total_value?.toLocaleString()} ${currentSummary.currency}`],
                      ['Buyer', currentSummary.buyer_name],
                      ['Seller', currentSummary.seller_name],
                      ['Location', currentSummary.location],
                      ['Grade', currentSummary.grade],
                      ['Delivery Terms', currentSummary.delivery_terms],
                      ['Payment Terms', currentSummary.payment_terms],
                    ].filter(([, val]) => val).map(([label, value]) => (
                      <div key={label} className="flex justify-between px-5 py-3">
                        <span className="text-sm text-[#64748B]">{label}</span>
                        <span className={`text-sm font-medium text-right max-w-[60%] ${label === 'Total Value' ? 'text-[#F59E0B]' : 'text-white'}`}>{value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="px-5 py-3 bg-[#1A2440] border-t border-[#1E293B]">
                    <p className="text-xs text-[#64748B]">Generated: {new Date(currentSummary.generated_at).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <TabsContent value="compliance" data-testid="compliance-content">
            <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden">
              {pendingCompliance.length === 0 ? (
                <div className="text-center py-12">
                  <ShieldAlert className="w-12 h-12 text-[#1E293B] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">No offers pending compliance review</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1A2440] border-b border-[#1E293B]">
                      <tr>
                        {['Mineral', 'Buyer', 'Seller', 'Offer Price', 'Quantity', 'Total Value', 'Status', 'Actions'].map(h => (
                          <th key={h} className="px-6 py-4 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pendingCompliance.map((offer) => (
                        <tr key={offer.id} className="border-b border-[#1E293B] hover:bg-white/[0.02] transition-colors" data-testid={`compliance-row-${offer.id}`}>
                          <td className="px-6 py-4 text-sm font-medium text-white">{offer.mineral_title}</td>
                          <td className="px-6 py-4 text-sm text-[#94A3B8]">{offer.buyer_name}</td>
                          <td className="px-6 py-4 text-sm text-[#94A3B8]">{offer.seller_name}</td>
                          <td className="px-6 py-4 text-sm text-white">${offer.offer_price?.toLocaleString()}</td>
                          <td className="px-6 py-4 text-sm text-[#94A3B8]">{offer.quantity}</td>
                          <td className="px-6 py-4 text-sm font-medium text-[#F59E0B]">${offer.total_value?.toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20">
                              {tStatus(offer.compliance_status || 'pending_review')}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <Button onClick={() => handleComplianceReview(offer.id, true)} data-testid={`approve-compliance-${offer.id}`} size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white">Approve</Button>
                              <Button onClick={() => handleComplianceReview(offer.id, false)} data-testid={`reject-compliance-${offer.id}`} size="sm" className="bg-transparent border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10">Reject</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="inspections" data-testid="inspections-content">
            <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden">
              {inspections.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 text-[#1E293B] mx-auto mb-4" />
                  <p className="text-[#64748B]">No inspections scheduled yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1A2440] border-b border-[#1E293B]">
                      <tr>
                        {['Warehouse', 'Status', 'Time Remaining', 'Assay Results', 'Actions'].map(h => (
                          <th key={h} className="px-6 py-4 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {inspections.map((inspection) => (
                        <tr key={inspection.id} className="border-b border-[#1E293B] hover:bg-white/[0.02]">
                          <td className="px-6 py-4">
                            <p className="font-medium text-white">{inspection.warehouse_name}</p>
                            <p className="text-sm text-[#64748B]">{inspection.warehouse_location}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${
                              inspection.status === 'completed' ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' :
                              inspection.status === 'scheduled' ? 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20' :
                              inspection.status === 'retest_pending' ? 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20' :
                              'bg-white/5 text-[#94A3B8] border-[#1E293B]'
                            }`}>{tStatus(inspection.status)}</span>
                          </td>
                          <td className="px-6 py-4">
                            {inspection.hours_remaining !== undefined ? (
                              <span className={`flex items-center gap-1 text-sm ${inspection.hours_remaining < 12 ? 'text-[#EF4444]' : 'text-[#94A3B8]'}`}>
                                <Clock className="w-4 h-4" />{inspection.hours_remaining.toFixed(1)}h
                              </span>
                            ) : <span className="text-[#64748B]">-</span>}
                          </td>
                          <td className="px-6 py-4">
                            {inspection.preliminary_assay_result ? (
                              <span className={`text-sm ${inspection.preliminary_meets_spec ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                                {inspection.preliminary_grade} ({inspection.preliminary_purity}%)
                              </span>
                            ) : <span className="text-[#64748B]">Pending</span>}
                          </td>
                          <td className="px-6 py-4">
                            {inspection.status === 'scheduled' && !inspection.preliminary_assay_result && (
                              <Dialog open={assayDialogOpen && selectedInspection?.id === inspection.id} onOpenChange={(open) => { setAssayDialogOpen(open); if (open) setSelectedInspection(inspection); }}>
                                <DialogTrigger asChild>
                                  <Button size="sm" className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120]"><FileText className="w-4 h-4 mr-1" />Submit Assay</Button>
                                </DialogTrigger>
                                <DialogContent className="bg-[#121A2F] border-[#1E293B]">
                                  <DialogHeader><DialogTitle className="text-white">Submit Preliminary Assay Results</DialogTitle></DialogHeader>
                                  <div className="space-y-4 mt-4">
                                    <div><Label className="text-[#94A3B8]">Grade Result *</Label><Input value={assayForm.grade_result} onChange={(e) => setAssayForm({ ...assayForm, grade_result: e.target.value })} placeholder="e.g., 25% Ta2O5" className="bg-[#0B1120] border-[#1E293B] text-white" /></div>
                                    <div><Label className="text-[#94A3B8]">Purity Percentage *</Label><Input type="number" value={assayForm.purity_percentage} onChange={(e) => setAssayForm({ ...assayForm, purity_percentage: e.target.value })} placeholder="e.g., 25.5" className="bg-[#0B1120] border-[#1E293B] text-white" /></div>
                                    <div><Label className="text-[#94A3B8]">Inspector Name *</Label><Input value={assayForm.inspector_name} onChange={(e) => setAssayForm({ ...assayForm, inspector_name: e.target.value })} placeholder="Inspector's full name" className="bg-[#0B1120] border-[#1E293B] text-white" /></div>
                                    <div className="flex items-center space-x-2"><Checkbox id="meets_spec" checked={assayForm.meets_specification} onCheckedChange={(checked) => setAssayForm({ ...assayForm, meets_specification: checked })} /><Label htmlFor="meets_spec" className="text-[#94A3B8]">Meets Specification</Label></div>
                                    <div><Label className="text-[#94A3B8]">Notes</Label><Textarea value={assayForm.notes} onChange={(e) => setAssayForm({ ...assayForm, notes: e.target.value })} placeholder="Additional notes..." rows={3} className="bg-[#0B1120] border-[#1E293B] text-white" /></div>
                                    <Button onClick={() => handleSubmitAssay(false)} className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold">Submit Preliminary Results</Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                            {inspection.retest_requested && !inspection.final_assay_result && (
                              <Dialog>
                                <DialogTrigger asChild><Button size="sm" className="bg-[#1A2440] text-white border border-[#1E293B] hover:bg-white/5" onClick={() => setSelectedInspection(inspection)}>Submit Final Assay</Button></DialogTrigger>
                                <DialogContent className="bg-[#121A2F] border-[#1E293B]">
                                  <DialogHeader><DialogTitle className="text-white">Submit Final Assay Results (Re-test)</DialogTitle></DialogHeader>
                                  <div className="space-y-4 mt-4">
                                    <div><Label className="text-[#94A3B8]">Grade Result *</Label><Input value={assayForm.grade_result} onChange={(e) => setAssayForm({ ...assayForm, grade_result: e.target.value })} placeholder="e.g., 25% Ta2O5" className="bg-[#0B1120] border-[#1E293B] text-white" /></div>
                                    <div><Label className="text-[#94A3B8]">Purity Percentage *</Label><Input type="number" value={assayForm.purity_percentage} onChange={(e) => setAssayForm({ ...assayForm, purity_percentage: e.target.value })} placeholder="e.g., 25.5" className="bg-[#0B1120] border-[#1E293B] text-white" /></div>
                                    <div><Label className="text-[#94A3B8]">Inspector Name *</Label><Input value={assayForm.inspector_name} onChange={(e) => setAssayForm({ ...assayForm, inspector_name: e.target.value })} placeholder="Inspector's full name" className="bg-[#0B1120] border-[#1E293B] text-white" /></div>
                                    <div className="flex items-center space-x-2"><Checkbox id="meets_spec_final" checked={assayForm.meets_specification} onCheckedChange={(checked) => setAssayForm({ ...assayForm, meets_specification: checked })} /><Label htmlFor="meets_spec_final" className="text-[#94A3B8]">Meets Specification</Label></div>
                                    <div><Label className="text-[#94A3B8]">Notes</Label><Textarea value={assayForm.notes} onChange={(e) => setAssayForm({ ...assayForm, notes: e.target.value })} placeholder="Additional notes..." rows={3} className="bg-[#0B1120] border-[#1E293B] text-white" /></div>
                                    <Button onClick={() => handleSubmitAssay(true)} className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold">Submit Final Results</Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="fcos" data-testid="fcos-content">
            <FCOsAdminTab />
          </TabsContent>

          <TabsContent value="checklists" data-testid="checklists-content">
            <ChecklistsAdminTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
